import { Head, Link, useForm } from '@inertiajs/react';
import { useState, useMemo, useEffect } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import FormInput from '@/Components/FormInput';
import FormSelect from '@/Components/FormSelect';
import PageHeader from '@/Components/PageHeader';
import Alert from '@/Components/Alert';
import Badge from '@/Components/Badge';
import {
    ArrowLeftIcon,
    PlusIcon,
    TrashIcon,
    ClockIcon,
    UserGroupIcon,
    CheckIcon,
} from '@heroicons/react/24/outline';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function getDayName(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return DAYS[d.getDay()] || '';
}

export default function Edit({ timesheet, employees, locations, flash }) {
    const { data, setData, put, processing, errors } = useForm({
        date: timesheet.date ? timesheet.date.split('T')[0] : '',
        notes: timesheet.notes || '',
        is_holiday: timesheet.is_holiday || false,
        entries: timesheet.entries?.length
            ? timesheet.entries.map(e => ({
                employee_id: e.employee_id || e.employee?.id || '',
                work_location_id: e.work_location_id || e.work_location?.id || '',
                ord_time: e.ord_time ?? 8,
                overtime: e.overtime ?? 0,
                note: e.note || '',
                is_absent: e.is_absent || false,
                is_sick_leave: e.is_sick_leave || false,
            }))
            : [],
    });

    const [dayName, setDayName] = useState(timesheet.day_name || getDayName(data.date));

    const isRestDay = dayName === 'Sunday' || data.is_holiday;

    const handleDateChange = (val) => {
        setData('date', val);
        setDayName(getDayName(val));
    };

    // When switching to rest day, zero out all ORD times
    useEffect(() => {
        if (isRestDay) {
            const updated = data.entries.map(e => ({ ...e, ord_time: 0 }));
            setData('entries', updated);
        }
    }, [isRestDay]);

    // Entry management
    const addEntry = () => {
        setData('entries', [
            ...data.entries,
            { employee_id: '', work_location_id: '', ord_time: isRestDay ? 0 : 8, overtime: 0, note: '', is_absent: false, is_sick_leave: false },
        ]);
    };

    const removeEntry = (i) => {
        setData('entries', data.entries.filter((_, idx) => idx !== i));
    };

    const updateEntry = (i, field, val) => {
        const entries = [...data.entries];
        entries[i] = { ...entries[i], [field]: val };
        setData('entries', entries);
    };

    const addAllEmployees = () => {
        const existingIds = data.entries.map(e => String(e.employee_id));
        const newEntries = employees
            .filter(emp => !existingIds.includes(String(emp.id)))
            .map(emp => ({
                employee_id: emp.id,
                work_location_id: emp.work_location_id || locations?.[0]?.id || '',
                ord_time: isRestDay ? 0 : 8,
                overtime: 0,
                note: '',
                is_absent: false,
                is_sick_leave: false,
            }));
        setData('entries', [...data.entries, ...newEntries]);
    };

    // Computed OT breakdown
    const getOt125 = (overtime) => isRestDay ? 0 : Math.min(Number(overtime) || 0, 2);
    const getOt150 = (overtime) => isRestDay ? (Number(overtime) || 0) : Math.max((Number(overtime) || 0) - 2, 0);

    // Summary totals (exclude absent)
    const summary = useMemo(() => {
        return data.entries.reduce(
            (acc, entry) => {
                if (entry.is_absent || entry.is_sick_leave) return acc;
                const ot = Number(entry.overtime) || 0;
                acc.totalOrd += Number(entry.ord_time) || 0;
                acc.totalOt += ot;
                acc.totalOt125 += getOt125(ot);
                acc.totalOt150 += getOt150(ot);
                return acc;
            },
            { totalOrd: 0, totalOt: 0, totalOt125: 0, totalOt150: 0 }
        );
    }, [data.entries, isRestDay]);

    const submit = (e) => {
        e.preventDefault();
        put(`/timesheets/${timesheet.id}`);
    };

    return (
        <AppLayout>
            <Head title="Edit TimeSheet" />
            <PageHeader
                title="Edit TimeSheet"
                subtitle={
                    <span className="flex items-center gap-2">
                        <Link href="/timesheets" className="text-emerald-700 hover:text-emerald-800 hover:underline">TimeSheet</Link>
                        <span className="text-slate-400">/</span>
                        <span>Edit TimeSheet</span>
                        {dayName && (
                            <Badge variant="info">{dayName}</Badge>
                        )}
                    </span>
                }
                actions={
                    <Button href="/timesheets" variant="outline" size="sm">
                        <ArrowLeftIcon className="w-4 h-4" /> Back
                    </Button>
                }
            />

            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}
            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}

            <form onSubmit={submit}>
                {/* General Info */}
                <Card className="mb-6">
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">General Information</h3>
                    </div>
                    <CardBody className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <FormInput
                                label="Date"
                                type="date"
                                value={data.date}
                                onChange={e => handleDateChange(e.target.value)}
                                error={errors.date}
                                required
                            />
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Day</label>
                                <div className={`w-full rounded-xl border text-sm px-4 py-2.5 ${
                                    dayName === 'Sunday'
                                        ? 'border-orange-300 bg-orange-50 text-orange-700 font-medium'
                                        : 'border-slate-200 bg-slate-50 text-slate-600'
                                }`}>
                                    {dayName || '—'}
                                    {dayName === 'Sunday' && <span className="ml-2 text-[10px] uppercase tracking-wide">Rest Day</span>}
                                </div>
                            </div>
                            <FormInput
                                label="Notes"
                                value={data.notes}
                                onChange={e => setData('notes', e.target.value)}
                                error={errors.notes}
                                placeholder="Optional notes..."
                            />
                        </div>
                        {/* Holiday Checkbox */}
                        <div className="pt-1">
                            <label className={`inline-flex items-center gap-3 cursor-pointer select-none rounded-xl border px-4 py-3 transition ${
                                data.is_holiday ? 'border-orange-300 bg-orange-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                            }`}>
                                <input
                                    type="checkbox"
                                    checked={data.is_holiday}
                                    onChange={e => setData('is_holiday', e.target.checked)}
                                    className="w-4 h-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                                />
                                <div>
                                    <span className="text-sm font-medium text-slate-700">Public Holiday</span>
                                    <p className="text-[11px] text-slate-400 mt-0.5">Mark this day as a public holiday. ORD time will not apply.</p>
                                </div>
                            </label>
                        </div>
                    </CardBody>
                </Card>

                {/* Employee Entries */}
                <Card className="mb-6">
                    <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div className="flex items-center gap-2">
                            <UserGroupIcon className="w-5 h-5 text-slate-400" />
                            <h3 className="font-semibold text-slate-800">Employee Entries</h3>
                            {data.entries.length > 0 && (
                                <span className="text-xs text-slate-400">({data.entries.length} employees)</span>
                            )}
                            {isRestDay && (
                                <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-orange-100 text-orange-700 rounded-full">
                                    Rest Day — Overtime Only
                                </span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button type="button" variant="outline" size="xs" onClick={addAllEmployees}>
                                <CheckIcon className="w-3 h-3" /> Select All
                            </Button>
                            <Button type="button" variant="outline" size="xs" onClick={addEntry}>
                                <PlusIcon className="w-3 h-3" /> Add Employee
                            </Button>
                        </div>
                    </div>
                    <CardBody>
                        {data.entries.length === 0 && (
                            <p className="text-center text-sm text-slate-400 py-6">
                                No employees added. Click "Add Employee" or "Select All" to begin.
                            </p>
                        )}
                        <div className="space-y-4">
                            {data.entries.map((entry, i) => {
                                const emp = employees?.find(em => em.id == entry.employee_id);
                                const rowDisabled = entry.is_absent || entry.is_sick_leave;
                                const ordDisabled = isRestDay || rowDisabled;
                                const fieldDisabled = rowDisabled;
                                return (
                                    <div key={i} className={`p-4 rounded-xl border transition ${
                                        entry.is_absent
                                            ? 'bg-red-50/50 border-red-200'
                                            : entry.is_sick_leave
                                                ? 'bg-amber-50/50 border-amber-200'
                                                : 'bg-slate-50 border-slate-200'
                                    }`}>
                                        <div className="flex justify-between items-center mb-3">
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm font-medium text-slate-700">Employee #{i + 1}</span>
                                                <label className={`inline-flex items-center gap-1.5 cursor-pointer select-none px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                                                    entry.is_absent
                                                        ? 'bg-red-100 text-red-700'
                                                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                                }`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={entry.is_absent}
                                                        onChange={e => {
                                                            updateEntry(i, 'is_absent', e.target.checked);
                                                            if (e.target.checked) updateEntry(i, 'is_sick_leave', false);
                                                        }}
                                                        className="w-3.5 h-3.5 rounded border-slate-300 text-red-600 focus:ring-red-500"
                                                    />
                                                    Absent
                                                </label>
                                                <label className={`inline-flex items-center gap-1.5 cursor-pointer select-none px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                                                    entry.is_sick_leave
                                                        ? 'bg-amber-100 text-amber-700'
                                                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                                }`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={entry.is_sick_leave}
                                                        onChange={e => {
                                                            updateEntry(i, 'is_sick_leave', e.target.checked);
                                                            if (e.target.checked) updateEntry(i, 'is_absent', false);
                                                        }}
                                                        className="w-3.5 h-3.5 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                                                    />
                                                    Sick Leave
                                                </label>
                                            </div>
                                            <button type="button" onClick={() => removeEntry(i)} className="p-1.5 hover:bg-red-50 rounded-lg">
                                                <TrashIcon className="w-4 h-4 text-red-400" />
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-1">Employee</label>
                                                <div className={`w-full rounded-xl border text-sm px-4 py-2.5 ${
                                                    rowDisabled
                                                        ? 'border-red-200 bg-red-50 text-slate-400 line-through'
                                                        : 'border-slate-200 bg-white text-slate-800'
                                                }`}>
                                                    {emp ? `${emp.employee_code} — ${emp.full_name}` : (
                                                        <FormSelect
                                                            value={entry.employee_id}
                                                            onChange={e => updateEntry(i, 'employee_id', e.target.value)}
                                                            error={errors[`entries.${i}.employee_id`]}
                                                            disabled={fieldDisabled}
                                                        >
                                                            <option value="">Select employee...</option>
                                                            {employees?.map(em => (
                                                                <option key={em.id} value={em.id}>
                                                                    {em.employee_code} — {em.full_name}
                                                                </option>
                                                            ))}
                                                        </FormSelect>
                                                    )}
                                                </div>
                                            </div>
                                            <FormSelect
                                                label="Work Location"
                                                value={entry.work_location_id}
                                                onChange={e => updateEntry(i, 'work_location_id', e.target.value)}
                                                error={errors[`entries.${i}.work_location_id`]}
                                                disabled={fieldDisabled}
                                            >
                                                <option value="">Select location...</option>
                                                {locations?.map(loc => (
                                                    <option key={loc.id} value={loc.id}>
                                                        {loc.name}
                                                    </option>
                                                ))}
                                            </FormSelect>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                                    ORD Time {!ordDisabled && <span className="text-red-500">*</span>}
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.5"
                                                    value={ordDisabled ? 0 : entry.ord_time}
                                                    onChange={e => updateEntry(i, 'ord_time', e.target.value)}
                                                    disabled={ordDisabled}
                                                    className={`w-full rounded-xl border text-sm px-4 py-2.5 transition ${
                                                        ordDisabled
                                                            ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                                            : 'border-slate-200 text-slate-800 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20'
                                                    }`}
                                                />
                                                {errors[`entries.${i}.ord_time`] && <p className="mt-1 text-xs text-red-500">{errors[`entries.${i}.ord_time`]}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-1">Overtime</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.5"
                                                    value={fieldDisabled ? 0 : entry.overtime}
                                                    onChange={e => updateEntry(i, 'overtime', e.target.value)}
                                                    disabled={fieldDisabled}
                                                    className={`w-full rounded-xl border text-sm px-4 py-2.5 transition ${
                                                        fieldDisabled
                                                            ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                                            : 'border-slate-200 text-slate-800 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20'
                                                    }`}
                                                />
                                                {errors[`entries.${i}.overtime`] && <p className="mt-1 text-xs text-red-500">{errors[`entries.${i}.overtime`]}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-700 mb-1">Note</label>
                                                <input
                                                    type="text"
                                                    value={entry.note}
                                                    onChange={e => updateEntry(i, 'note', e.target.value)}
                                                    placeholder={fieldDisabled ? 'Absent' : 'Optional note...'}
                                                    disabled={fieldDisabled}
                                                    className={`w-full rounded-xl border text-sm px-4 py-2.5 transition ${
                                                        fieldDisabled
                                                            ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                                            : 'border-slate-200 text-slate-800 placeholder-slate-400 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20'
                                                    }`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {errors.entries && <p className="mt-2 text-xs text-red-500">{errors.entries}</p>}
                    </CardBody>
                </Card>

                {/* Summary removed */}

                <div className="flex gap-3">
                    <Button type="submit" disabled={processing}>
                        {processing ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button href="/timesheets" variant="outline">Cancel</Button>
                </div>
            </form>
        </AppLayout>
    );
}
